import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { apiCache } from '../../services/apiCache';
import { useAuthContext } from '../../contexts/AuthContext';
import WorkWidget from './WorkWidget';

const ConditionalWorkWidget: React.FC = () => {
  const { token, loading: authLoading } = useAuthContext();
  const [hasAccess, setHasAccess] = useState(false);
  const [hasActiveWork, setHasActiveWork] = useState(false);
  const [loading, setLoading] = useState(true);
  const hasCheckedAccess = useRef(false);

  useEffect(() => {
    // Only check access once when we have a token and auth is not loading
    if (token && !authLoading && !hasCheckedAccess.current) {
      hasCheckedAccess.current = true;
      checkAccess();
    }
  }, [token, authLoading]);

  useEffect(() => {
    // Check for active work periodically if user has access
    if (hasAccess) {
      checkActiveWork();
      const interval = setInterval(checkActiveWork, 300000); // Check every 5 minutes
      return () => clearInterval(interval);
    }
  }, [hasAccess]);

  const checkAccess = async () => {
    // Don't check if no token available or auth is still loading
    if (!token || authLoading) {
      setLoading(false);
      return;
    }
    
    try {
      const accessResponse = await api.get('/role-pages/access/my-assignments');
      const hasPageAccess = accessResponse.data.success && accessResponse.data.data.has_access;
      setHasAccess(hasPageAccess);
      
      if (hasPageAccess) {
        await checkActiveWork();
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const checkActiveWork = async () => {
    // Don't check if no token available or auth is still loading
    if (!token || authLoading) {
      return;
    }
    
    try {
      const response = await api.get('/my-assignments/active-all');
      const activeAssignments = response.data.success ? response.data.data : [];
      
      setHasActiveWork(activeAssignments && activeAssignments.length > 0);
    } catch (error) {
      console.error('Error checking active work:', error);
      setHasActiveWork(false);
    }
  };

  if (loading || !hasAccess || !hasActiveWork) {
    return null;
  }

  return <WorkWidget />;
};

export default ConditionalWorkWidget;